import LiftCard from '@/components/lift-control/LiftCard';
import { RouterRootState } from 'connected-react-router';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import AssignedTab from '../../../components/assignWizard/AssignedTab';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import FloorColumns from '../../../models/ColumnDefinitions/FloorColumn';
import LiftIoDeviceColumns from '../../../models/ColumnDefinitions/LiftIoDeviceColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { Lift as LiftModel } from '../../../models/Lift';
import { ListQuery } from '../../../models/ListQuery';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import FloorSearch from '../../../models/search/FloorSearch';
import IOModuleSearch from '../../../models/search/IOModuleSearch';
import { TabSummary } from '../../../models/TabSummary';
import { removeColumns } from '../../../models/utilities/columnHelper';
import {  getLiftById, getLiftDetails, getLiftIODevices, getLiftNamesWithPagination, updateLift } from '../../../services/liftService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware/lifts';
const itemPath = '/access-control/hardware/lift/';

const initialLift: LiftModel = {
    id: 0,
    name: '',
    site: '',
    building: '',
    level: 0,
    floors: 0,
    readerId: 0,
    readerName: '',
    ioModules: 0,
    description: ''
}

const Lift: FC = () => {
    const { translate } = useLocale();
    const history = useHistory();
    const dialog = useDialog();
    const [busy, setBusy] = useState(false);
    const initialTabs: TabSummary[] = [
        { label: translate.byKey(TranslationKeys.ioDevices), badge: 0, showInitial: false, isActive: true },
        { label: translate.byKey(TranslationKeys.floors), badge: 0, showInitial: false, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: false, isActive: true },
    ]
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<LiftModel>(initialLift);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const tabContentList: ContentWrapper[] = [
        {
            content: <AssignedTab
                key={"LiflIODevices-tab"}
                id={itemId}
                column={removeColumns(LiftIoDeviceColumns, ["type", "site"])}
                searchColumn={IOModuleSearch}
                title={translate.byKey(TranslationKeys.assignedIoDevices)}
                dataMethod={(listParameters?: ListQuery) => getLiftIODevices({ ...listParameters, filters: appendFilter(itemId, 'Lift', 'lift.id', listParameters?.filters ?? []) })}>
            </AssignedTab>
        },
        {
            content: <AssignedTab
                key={"LiftFloors-tab"}
                id={itemId}
                column={removeColumns(FloorColumns, ['site', 'building'])}
                searchColumn={removeColumns(FloorSearch, ['site', 'building'])}
                title={translate.byKey(TranslationKeys.assignedFloors)}
                dataMethod={(listParameters?: ListQuery) => getLiftDetails({ ...listParameters, filters: appendFilter(itemId, 'Lift', 'lift.id', listParameters?.filters ?? []) })}>
            </AssignedTab>
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ]

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const topic: Topic = {
        accessor: 'name',
        title: translate.byKey(TranslationKeys.lifts),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const reloadPage = async () => {
        try {
            let lift: any
            setTabs([]);
            if (itemId) {
                lift = await getLiftById(itemId);
                setSelectedItem({ label: lift.name, value: lift.id.toString() });
                setCurrentItem(lift);
                const updatedTabs = updateTabBadgeTotal(0, lift.ioModules, updateTabBadgeTotal(1, lift.floors, initialTabs));
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialLift);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.lifts) },
                { value: itemPath + `${itemId}`, displayName: `${lift?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const saveLift = (values: any) => {
        const lift: LiftModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            siteId: itemId ? values.siteId : site.value,
            buildingId: values.buildingId,
            building: values.building,
            site: itemId ? values.site : site.label,
            level: values.startingFloor,
            floors: values.numberOfFloors,
            description: values.description
        };

        saveItem(lift);
    }

    const saveItem = async (item: LiftModel) => {
        setBusy(true);
        try {
            await updateLift(item);
            toast.success(translate.byKey(TranslationKeys.liftSaved));
            reloadPage();

        } catch (error) {
            dialog.pushError({title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error)});
        } finally {
            setBusy(false);
        }
    };

    return <div className="flex h-full">
        <TopicContextProvider topic={topic} dataMethod={getLiftNamesWithPagination} itemId={itemId} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveLift}
                    tabs={tabs}
                    initialValues={currentItem}>
                    <LiftCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </div>
}

export default Lift;